import {
  Button, Form, Input, InputNumber, message, Modal, Popconfirm, Select, Space, Table, Tag, Typography,
} from 'antd';
import {
  AppstoreOutlined, BankOutlined, CalendarOutlined, DashboardOutlined, DeleteOutlined,
  EditOutlined, FileProtectOutlined, FileTextOutlined, FolderOpenOutlined, PlusOutlined,
  SettingOutlined, TeamOutlined, UploadOutlined, UsergroupAddOutlined, UserOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { createMenu, deleteMenu, listMenus, updateMenu } from '../../services/adminCoreApi';

const { Text, Title } = Typography;

const MENU_ICON_OPTIONS = [
  { value: 'Dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
  { value: 'ClientManagement', label: 'Client Management', icon: <BankOutlined /> },
  { value: 'EmployerManagement', label: 'Employer / Referral', icon: <TeamOutlined /> },
  { value: 'Candidates', label: 'Candidates', icon: <UsergroupAddOutlined /> },
  { value: 'Jobs', label: 'Jobs', icon: <FileTextOutlined /> },
  { value: 'Submissions', label: 'Submissions', icon: <FileTextOutlined /> },
  { value: 'Onboarding', label: 'Onboarding', icon: <UserOutlined /> },
  { value: 'Assignments', label: 'Assignments', icon: <AppstoreOutlined /> },
  { value: 'Timesheet', label: 'Timesheet', icon: <CalendarOutlined /> },
  { value: 'Campaign', label: 'Campaign', icon: <TeamOutlined /> },
  { value: 'FileProtectOutlined', label: 'PO / MSA', icon: <FileProtectOutlined /> },
  { value: 'FolderOpenOutlined', label: 'Batches', icon: <FolderOpenOutlined /> },
  { value: 'BankOutlined', label: 'Billing / Finance', icon: <BankOutlined /> },
  { value: 'SettingOutlined', label: 'Settings / Admin', icon: <SettingOutlined /> },
  { value: 'UploadOutlined', label: 'Upload', icon: <UploadOutlined /> },
  { value: 'AppstoreOutlined', label: 'General Module', icon: <AppstoreOutlined /> },
];

function IconOption({ option }) {
  return (
    <Space size={8}>
      <span style={{ display: 'inline-flex', fontSize: 16 }}>{option.icon}</span>
      <span>{option.label}</span>
      <Text type="secondary" style={{ fontSize: 11 }}>{option.value}</Text>
    </Space>
  );
}

function updateStoredSidebarMenu(menuKey, values) {
  const raw = localStorage.getItem('menu');
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed) ? parsed : parsed?.menu;
    if (!Array.isArray(items)) return;

    const targetKey = String(menuKey ?? values.menu_key ?? '').trim().toLowerCase();
    const patchItems = (list) => list.map((item) => {
      const currentKey = String(item?.key ?? '').trim().toLowerCase();
      const next = currentKey === targetKey
        ? {
          ...item,
          icon: values.icon ?? item.icon,
          label: values.menu_name ?? item.label,
          status: String(values.status ?? item.status ?? 'ACTIVE').toLowerCase(),
        }
        : item;
      return Array.isArray(next?.children)
        ? { ...next, children: patchItems(next.children) }
        : next;
    });

    const nextItems = patchItems(items);
    localStorage.setItem('menu', JSON.stringify(Array.isArray(parsed) ? nextItems : { ...parsed, menu: nextItems }));
    window.dispatchEvent(new Event('menu:updated'));
  } catch {
    // A malformed/stale login cache should not block saving an admin menu.
  }
}

export default function MenusSection() {
  const [form] = Form.useForm();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  async function loadMenus() {
    setLoading(true);
    try {
      setMenus(await listMenus());
    } catch (error) {
      message.error(error.message || 'Unable to load menus');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadMenus(); }, []);

  const parentOptions = useMemo(() => menus
    .filter((menu) => !editing || menu.menu_id !== editing.menu_id)
    .map((menu) => ({ label: menu.menu_name, value: menu.menu_id })), [editing, menus]);

  function showCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ menu_type: 'MENU', display_order: menus.length + 1, status: 'ACTIVE' });
    setOpen(true);
  }

  function showEdit(menu) {
    setEditing(menu);
    form.setFieldsValue(menu);
    setOpen(true);
  }

  async function submit(values) {
    setSaving(true);
    try {
      if (editing) {
        await updateMenu(editing.menu_id, values);
        updateStoredSidebarMenu(editing.menu_key, values);
      } else {
        await createMenu(values);
      }
      message.success(editing ? 'Menu updated' : 'Menu created');
      setOpen(false);
      await loadMenus();
    } catch (error) {
      message.error(error.message || 'Unable to save menu');
    } finally {
      setSaving(false);
    }
  }

  async function remove(menuId) {
    try {
      await deleteMenu(menuId);
      message.success('Menu deleted');
      await loadMenus();
    } catch (error) {
      message.error(error.message || 'Unable to delete menu');
    }
  }

  const columns = [
    { title: 'Name', dataIndex: 'menu_name' },
    { title: 'Key', dataIndex: 'menu_key', render: (value) => <Tag>{value}</Tag> },
    { title: 'Route / API', dataIndex: 'api_url' },
    { title: 'Icon', dataIndex: 'icon', width: 170, render: (value) => <Tag>{value || 'Auto'}</Tag> },
    { title: 'Type', dataIndex: 'menu_type', width: 110 },
    { title: 'Order', dataIndex: 'display_order', width: 80 },
    { title: 'Status', dataIndex: 'status', width: 100, render: (value) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '', key: 'actions', width: 90, render: (_, row) => (
        <Space size={4}>
          <Button aria-label="Edit menu" size="small" icon={<EditOutlined />} onClick={() => showEdit(row)} />
          <Popconfirm title="Delete menu?" onConfirm={() => remove(row.menu_id)}>
            <Button aria-label="Delete menu" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <div><Title level={4}>Menus</Title><Text type="secondary">Manage records in the MongoDB menus collection.</Text></div>
        <Button type="primary" icon={<PlusOutlined />} onClick={showCreate}>Add Menu</Button>
      </div>
      <Table rowKey="menu_id" loading={loading} columns={columns} dataSource={menus} size="small" pagination={{ pageSize: 10 }} />
      <Modal title={editing ? 'Edit Menu' : 'Add Menu'} open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} confirmLoading={saving} destroyOnHidden>
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="menu_name" label="Menu name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="menu_key" label="Menu key" rules={[{ required: true }]}><Input placeholder="jobs" /></Form.Item>
          <Form.Item name="api_url" label="Route / API URL"><Input placeholder="/jobs" /></Form.Item>
          <Form.Item name="component_name" label="Component"><Input placeholder="Jobs" /></Form.Item>
          <div className="admin-form-row">
            <Form.Item name="menu_type" label="Type"><Select options={[{ value: 'MENU' }, { value: 'FOOTER' }, { value: 'CHILD' }]} /></Form.Item>
            <Form.Item name="parent_menu_id" label="Parent"><Select allowClear options={parentOptions} /></Form.Item>
          </div>
          <div className="admin-form-row">
            <Form.Item name="display_order" label="Display order"><InputNumber min={0} /></Form.Item>
            <Form.Item name="status" label="Status"><Select options={[{ value: 'ACTIVE' }, { value: 'INACTIVE' }]} /></Form.Item>
          </div>
          <Form.Item
            name="icon"
            label="Sidebar icon"
            extra="Saved in the menus collection and used dynamically in the application sidebar."
          >
            <Select
              allowClear
              showSearch
              placeholder="Select sidebar icon"
              optionFilterProp="label"
              options={MENU_ICON_OPTIONS.map(({ value, label }) => ({ value, label }))}
              optionRender={(selectOption) => {
                const value = selectOption?.value ?? selectOption?.data?.value;
                const option = MENU_ICON_OPTIONS.find((item) => item.value === value);
                return option ? <IconOption option={option} /> : value;
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
}
